// Layer 3 (Transformation): one June-to-date ore-tonnage read over the plant
// state (D14). Pure — no React, no engine mutation.
//
// The demo path used to publish three different June tonnages: the landing's
// performance series (14,121 dst through 08 Jun), `/cost`'s closed-month
// build-up (12,372 dst, the seven readings stamped 01–07 Jun) and `/planning`'s
// typed `M4_DAILY_ACTUALS` sum (13,760 st). Each read a different source on a
// different basis. This module is the one source: every June tonnage on the
// landing, `/cost`, `/planning` and `/reports` is this function's read.
//
// D25 (orchestrator, 24 Sep 2026, from the K12c review): June-to-date tonnage is
// **complete production days only**. The plant state is the measured daily ore
// series `rs-ore-td` (the series behind `rd-kpi-ore-td`, D13/D14) — never a
// typed per-day literal. Each reading is stamped at the start of its production
// day (18:00 the evening before) and covers that day's night shift (18:00–06:00)
// and day shift (06:00–18:00); the seed publishes it whole once its stamp
// passes. A production day counts only once its day shift has closed
// (`stamp + 24 h <= cursor`). The open production day is in no total and is
// never pro-rated. At the demo cursor (08 Jun 11:30) that is production days
// 1–7 Jun — 12,401 dst against plan 7 × 1,440 = 10,080 dst — and the open
// 08 Jun day shift (1,720 dst published whole) is excluded.
//
// Round 1's `closed` / `to-cursor` pair is gone; there is one window, and its
// label names it: `1–7 Jun`. The map
// (`docs/plans/komatsu-demo/map-k12cd-…`) records the stamps; the logic docs and
// probes lock the figure.
//
// K21 (#719): `juneWtdDays` applies the same D25 complete-day rule to the plant
// week (`weekStart` → the last closed production day), so the week-to-date ore
// is 2–7 Jun, 10,652 dst, the figure `/cost` and Metal accounting publish — the
// one WTD window `/meeting`, the Overview and Metal accounting all read.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { diffMinutes, productionDayOf, weekStart } from '@/versions/v1/shared/clock.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';

/** The one daily plan constant: the ramp target every plan figure reads.
 *  `/cost`'s `BUDGET_TONNES_PER_DAY` and the landing's `MTD_DAILY_PLAN_DST`
 *  both alias this value, so a month plan and a week plan cannot drift. */
export const JUNE_DAILY_PLAN_DST = 1440;

export type JuneTonnageDay = {
  /** The daily reading's stamp — the 18:00 of the evening before a production
   *  day, so the calendar date is the previous day for a production-day read. */
  stamp: SeriesTime;
  /** The production day the reading belongs to, e.g. `2026-06-08`. */
  productionDate: string;
  /** Calendar date of the stamp, e.g. `2026-06-07`. */
  stampDate: string;
  /** The production day's shift window closes at this stamp — `stamp + 24 h`. */
  closedAt: SeriesTime;
  dst: number;
};

export type JuneTonnageRead = {
  /** The June-to-date ore tonnage in dry short tons. */
  tonnesDst: number;
  days: readonly JuneTonnageDay[];
  dayCount: number;
  /** The plan for the same window: `dayCount × JUNE_DAILY_PLAN_DST`. */
  planDst: number;
  /** Human window name for the glass — e.g. `1–7 Jun` (complete production days). */
  windowLabel: string;
  /** Human plan name for the glass — e.g. `plan 1–7 Jun`. */
  planLabel: string;
  provenance: string;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayLabel(date: string, pad = false): string {
  const [, m, d] = date.split('-').map(Number);
  const day = pad ? String(d).padStart(2, '0') : String(d);
  return `${day} ${MONTHS[(m ?? 1) - 1] ?? ''}`;
}

/** `1–7 Jun` for a window of consecutive days in one month; one day reads `7 Jun`. */
function rangeLabel(from: string, to: string): string {
  if (from === to) return dayLabel(from);
  const [, fromMonth] = from.split('-').map(Number);
  const [, toMonth] = to.split('-').map(Number);
  if (fromMonth === toMonth) return `${Number(from.split('-')[2])}–${dayLabel(to)}`;
  return `${dayLabel(from)}–${dayLabel(to)}`;
}

/** Every published production-day reading of `rs-ore-td` at the cursor whose day
 *  shift has closed, oldest first, across month boundaries (D25). The cursor's
 *  own production day is open and is excluded. `/planning`'s seven-day rate
 *  sample reads the last seven of these (K16), so the forecast rate and the
 *  June-to-date actual are one measured series. */
export function completeProductionDays(state: SessionState): JuneTonnageDay[] {
  const view = seriesAt('rs-ore-td', state);
  return view.points
    .filter((point): point is { at: SeriesTime; value: number } => point.value !== null)
    .map((point) => {
      const { date, end } = productionDayOf(point.at);
      return {
        stamp: point.at,
        productionDate: date,
        stampDate: point.at.slice(0, 10),
        closedAt: end,
        dst: point.value,
      };
    })
    // D25: a production day counts once its day shift has closed.
    .filter((day) => diffMinutes(day.closedAt, state.cursor) <= 0)
    .sort((a, b) => a.stamp.localeCompare(b.stamp));
}

/** Every published June production-day reading at the cursor whose day shift has
 *  closed, oldest first. The cursor's own production day is open at 11:30 and is
 *  excluded; the month's first production day (reading stamped 31 May 18:00) is
 *  included. */
export function juneMtdDays(state: SessionState): JuneTonnageDay[] {
  const month = productionDayOf(state.cursor).date.slice(0, 7);
  return completeProductionDays(state).filter((day) => day.productionDate.slice(0, 7) === month);
}

/** The cursor week's complete production days, oldest first — the plant-week
 *  twin of `juneMtdDays` and the one week-to-date window on the demo path
 *  (K21 / D25, #719). `weekStart` is Sunday 18:00 before the week's Monday, so
 *  at the demo cursor the window is production days 2–7 Jun: the six readings
 *  stamped 01–06 Jun 18:00, 10,652 dst. The open 08 Jun day is in no total.
 *
 *  Metal accounting's `wtdWindow` (`modules/ingestion/periodBalance.ts`),
 *  `/meeting`'s performance tab and the Overview's plan-achievement and
 *  by-area columns all read this function, so every WTD figure on the page
 *  sums the same six days. The caller bounds a series mean with
 *  `[weekStart(state.cursor), last.closedAt)`. */
export function juneWtdDays(state: SessionState): JuneTonnageDay[] {
  const from = weekStart(state.cursor);
  return completeProductionDays(state).filter((day) => diffMinutes(day.stamp, from) >= 0);
}

/** One June-to-date tonnage read over the plant state: the complete production
 *  days of the cursor's month (D25). */
export function juneMtdTonnes(state: SessionState): JuneTonnageRead {
  return readFromDays(
    juneMtdDays(state),
    '[derived] measured daily ore series rs-ore-td, sum of the complete production days in the cursor month',
  );
}

/** One week-to-date tonnage read: the plant week's complete production days
 *  (`juneWtdDays`), the twin of `juneMtdTonnes` (K21 / D25, #719). The window
 *  name and the plan come from the same day count, so `/reports`' WTD row and
 *  its MTD row beneath it read one shape: `10,652 dst (2–7 Jun · 6 complete
 *  production days) vs 8,640 dst plan`. */
export function juneWtdTonnes(state: SessionState): JuneTonnageRead {
  return readFromDays(
    juneWtdDays(state),
    '[derived] measured daily ore series rs-ore-td, sum of the plant week\'s complete production days',
  );
}

/** One tonnage read shape over an already-selected day list — the plan is the
 *  day count × the one daily plan constant, and the window label the days it
 *  covers, so MTD and WTD cannot drift apart. */
function readFromDays(days: readonly JuneTonnageDay[], provenance: string): JuneTonnageRead {
  const tonnesDst = days.reduce((sum, day) => sum + day.dst, 0);
  const dayCount = days.length;
  const first = days[0];
  const last = days[days.length - 1];
  const windowLabel = first && last ? rangeLabel(first.productionDate, last.productionDate) : 'no complete days';
  const planLabel = first && last ? `plan ${rangeLabel(first.productionDate, last.productionDate)}` : `plan ${dayCount} days`;
  return {
    tonnesDst,
    days,
    dayCount,
    planDst: dayCount * JUNE_DAILY_PLAN_DST,
    windowLabel,
    planLabel,
    provenance,
  };
}
